import logger from '../utils/logger.js';
import React, { useState } from 'react';
import {
  getEvaluationMuiColor,
  getIntentEvaluationForDisplay,
  intentHasPersonaResultForDisplay,
  shouldShowIntentResult,
} from '../utils/intentEvaluationDisplay.js';
import {
  Box,
  Chip,
  Collapse,
  Typography,
  IconButton,
  Card,
  CardContent,
  Divider,
  Alert,
  Button,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Psychology as PsychologyIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Computer as ComputerIcon,
  Person as PersonIcon,
  ListAlt as ListAltIcon,
  Science as ScienceIcon,
  Description as DescriptionIcon,
  ContentCut as ContentCutIcon,
  Image as ImageIcon,
  AltRoute as AltRouteIcon
} from '@mui/icons-material';

/** Pretty-print DICOM wire format (leading "0" + JSON) for UI display. */
function formatDicomMessageForDisplay(raw) {
  if (typeof raw !== 'string' || !raw.length) return { pretty: '', raw };
  if (raw.startsWith('0') && raw.length > 1) {
    try {
      const parsed = JSON.parse(raw.slice(1));
      return { pretty: JSON.stringify(parsed, null, 2), raw };
    } catch {
      return { pretty: raw, raw };
    }
  }
  return { pretty: raw, raw };
}

function getIntentTypeLabel(intent) {
  if (intent.isSurgeryIntent) return 'Surgery';
  if (intent.isPatientSelectIntent) return 'Patient select';
  if (intent.isLabResultsIntent) return 'Lab results';
  if (intent.isEvalReportIntent) return 'Eval report';
  if (intent.isMedicalHistoryIntent) return 'Medical history';
  if (intent.isSoftwareIntent) return 'Software';
  return 'Normal';
}

function IntentTypeIcon({ intent }) {
  const label = getIntentTypeLabel(intent);
  if (intent.isPatientSelectIntent) {
    return (
      <Tooltip title={label} arrow placement="top">
        <ListAltIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      </Tooltip>
    );
  }
  if (intent.isLabResultsIntent) {
    return (
      <Tooltip title={label} arrow placement="top">
        <ScienceIcon sx={{ fontSize: 18, color: 'success.main' }} />
      </Tooltip>
    );
  }
  if (intent.isEvalReportIntent) {
    return (
      <Tooltip title={label} arrow placement="top">
        <DescriptionIcon sx={{ fontSize: 18, color: 'warning.main' }} />
      </Tooltip>
    );
  }
  if (intent.isMedicalHistoryIntent) {
    return (
      <Tooltip title={label} arrow placement="top">
        <PersonIcon sx={{ fontSize: 18, color: 'primary.main' }} />
      </Tooltip>
    );
  }
  if (intent.isSurgeryIntent) {
    return (
      <Tooltip title={label} arrow placement="top">
        <ContentCutIcon sx={{ fontSize: 18, color: 'error.main' }} />
      </Tooltip>
    );
  }
  if (intent.isSoftwareIntent) {
    return (
      <Tooltip title={label} arrow placement="top">
        <ComputerIcon sx={{ fontSize: 18, color: 'info.main' }} />
      </Tooltip>
    );
  }
  return null;
}

function getAlternativeIntentOf(intent) {
  if (!intent || typeof intent !== 'object') return '';
  return (
    intent.alternative_of ||
    intent.alternativeOf ||
    intent.primary_object ||
    intent.primaryObject ||
    ''
  );
}

function isAlternativeIntent(intent) {
  if (!intent || typeof intent !== 'object') return false;
  return intent.is_alternative_intent === true || intent.isAlternativeIntent === true;
}

function AlternativeIntentIcon({ intent }) {
  if (!isAlternativeIntent(intent)) return null;
  const alternativeOf = getAlternativeIntentOf(intent);
  const tooltipText = alternativeOf
    ? `Alternative intent of "${alternativeOf}"`
    : 'Alternative intent';
  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <AltRouteIcon sx={{ fontSize: 18, color: 'warning.main' }} />
    </Tooltip>
  );
}

function getIntentDedupKey(intent) {
  return JSON.stringify({
    action: intent?.action ?? null,
    object: intent?.object ?? null,
    modifier: intent?.modifier ?? null,
    measurement: intent?.measurement ?? null,
    isSurgeryIntent: intent?.isSurgeryIntent === true,
    isSoftwareIntent: intent?.isSoftwareIntent === true,
    isPatientSelectIntent: intent?.isPatientSelectIntent === true,
    isLabResultsIntent: intent?.isLabResultsIntent === true,
    isEvalReportIntent: intent?.isEvalReportIntent === true
  });
}

const IntentDisplay = ({ intents = [], softwareIntents = [], surgeryIntents = [], patientSelectIntents = [], labResultsIntents = [], evalReportIntents = [], legacyIntent = null, softwareIntent = null, patientSelect = null, dicomMessage = null }) => {
  const [expanded, setExpanded] = useState(false);
  const [dicomExpanded, setDicomExpanded] = useState(false);

  logger.info('🔍 IntentDisplay received:', {
    intents: intents,
    softwareIntents: softwareIntents,
    surgeryIntents: surgeryIntents,
    patientSelectIntents: patientSelectIntents,
    labResultsIntents: labResultsIntents,
    evalReportIntents: evalReportIntents,
    legacyIntent: legacyIntent,
    softwareIntent: softwareIntent,
    dicomMessage: dicomMessage ? '(set)' : null
  });

  const allIntentsRaw = [
    ...(intents || []).map(intent => ({ ...intent, isSoftwareIntent: intent.isSoftwareIntent || false, isPatientSelectIntent: false, isLabResultsIntent: false, isEvalReportIntent: false })),
    ...(softwareIntents || []).map(intent => ({ ...intent, isSoftwareIntent: true, isPatientSelectIntent: false, isLabResultsIntent: false, isEvalReportIntent: false })),
    ...(surgeryIntents || []).map(intent => ({ ...intent, isSurgeryIntent: true, isSoftwareIntent: false, isPatientSelectIntent: false, isLabResultsIntent: false, isEvalReportIntent: false })),
    ...(patientSelectIntents || []).map(intent => ({ ...intent, isSoftwareIntent: false, isPatientSelectIntent: true, isLabResultsIntent: false, isEvalReportIntent: false })),
    ...(labResultsIntents || []).map(intent => ({ ...intent, isSoftwareIntent: false, isPatientSelectIntent: false, isLabResultsIntent: true, isEvalReportIntent: false })),
    ...(evalReportIntents || []).map(intent => ({ ...intent, isSoftwareIntent: false, isPatientSelectIntent: false, isLabResultsIntent: false, isEvalReportIntent: true })),
    ...((intents || []).length === 0 && (surgeryIntents || []).length === 0 && (patientSelectIntents || []).length === 0 && (labResultsIntents || []).length === 0 && (evalReportIntents || []).length === 0 && legacyIntent ? [{ ...legacyIntent, isSoftwareIntent: false, isPatientSelectIntent: false, isLabResultsIntent: false, isEvalReportIntent: false }] : []),
    ...((softwareIntents || []).length === 0 && softwareIntent ? [{ ...softwareIntent, isSoftwareIntent: true, isPatientSelectIntent: false, isLabResultsIntent: false, isEvalReportIntent: false }] : [])
  ].filter(Boolean);

  const seenIntentKeys = new Set();
  const allIntents = allIntentsRaw.filter((intent) => {
    const key = getIntentDedupKey(intent);
    if (seenIntentKeys.has(key)) return false;
    seenIntentKeys.add(key);
    return true;
  });

  logger.info('🎯 Combined allIntents:', allIntents);

  const hasPatientSelect = patientSelect && (patientSelect.filteredPatientIds?.length > 0);
  const hasDicomAggregate = typeof dicomMessage === 'string' && dicomMessage.length > 0;
  if (allIntents.length === 0 && !hasPatientSelect && !hasDicomAggregate) {
    return null;
  }

  const hasPersonaResults = allIntents.some(intent => intent.hasPersonaResult);
  const dicomFormatted = hasDicomAggregate ? formatDicomMessageForDisplay(dicomMessage) : null;

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        role="button"
        aria-label={expanded ? 'Collapse intents' : 'Expand intents'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        <PsychologyIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography variant="caption" color="text.secondary">
          {allIntents.length > 0
            ? `Detected ${allIntents.length} intent${allIntents.length !== 1 ? 's' : ''}${hasPersonaResults ? ' (with patient data)' : ''}${hasDicomAggregate ? ' · DICOM payload' : ''}`
            : hasPatientSelect
              ? `Patient select${hasDicomAggregate ? ' · DICOM payload' : ''}`
              : hasDicomAggregate
                ? 'DICOM viewer aggregate payload'
                : ''}
        </Typography>
        <IconButton 
          size="small" 
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          sx={{ ml: 'auto' }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {hasPatientSelect && (
          <Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" gutterBottom>
              Patient select
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Filtered: [{((patientSelect?.filteredPatientIds) || []).join(', ')}]
            </Typography>
          </Box>
        )}
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {allIntents.map((intent, index) => {
            const evaluationDisplay = getIntentEvaluationForDisplay(intent);
            const showPersonaResults = intentHasPersonaResultForDisplay(intent)
              && !intent.needs_clarification
              && intent.prerequisites_met !== false
              && intent.mode_blocked !== true
              && !intent.isSoftwareIntent
              && !intent.isPatientSelectIntent
              && !intent.isLabResultsIntent
              && !intent.isEvalReportIntent;

            return (
            <Card key={index} variant="outlined" sx={{ bgcolor: 'background.paper' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                  <IntentTypeIcon intent={intent} />
                  <AlternativeIntentIcon intent={intent} />
                  {intent.isMedicalHistoryIntent ? (
                    <Chip 
                      label={`Ask about: ${intent.object}`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  ) : (
                    <>
                      <Chip 
                        label={`Action: ${intent.action || 'N/A'}`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                      <Chip 
                        label={`Object: ${intent.object || 'N/A'}`} 
                        size="small" 
                        color="secondary" 
                        variant="outlined"
                      />
                    </>
                  )}
                  {intent.isSoftwareIntent && intent.modifier && !intent.measurement && !intent.bad_modifier && (
                    <Chip 
                      label={`Modifier: ${intent.modifier}`} 
                      size="small" 
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {intent.isSoftwareIntent && intent.measurement && !intent.bad_modifier && (
                    <Chip 
                      label={`Measurement: ${intent.measurement.value} ${intent.measurement.unit}`} 
                      size="small" 
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {intent.isSoftwareIntent && intent.bad_modifier && (
                    <Chip 
                      label="Invalid Modifier" 
                      size="small" 
                      color="error"
                      variant="outlined"
                    />
                  )}
                  {intent.needs_clarification && (
                    <Chip 
                      label="Needs Clarification" 
                      size="small" 
                      color="warning"
                      variant="outlined"
                    />
                  )}
                  {intent.mode_blocked === true && (
                    <Chip
                      label="Not available in this mode"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                  {intent.prerequisites_met === false && !intent.needs_clarification && intent.mode_blocked !== true && (
                    <Chip 
                      label="Prerequisites Not Met" 
                      size="small" 
                      color="error"
                      variant="outlined"
                    />
                  )}
                  {intent.isSoftwareIntent && !intent.needs_clarification && !intent.bad_modifier && intent.prerequisites_met !== false && intent.mode_blocked !== true && (
                    <Chip 
                      label="Valid" 
                      size="small" 
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {!intent.isSoftwareIntent && intent.prerequisites_met !== false && intent.mode_blocked !== true && !intent.needs_clarification && intentHasPersonaResultForDisplay(intent) && (
                    <Chip 
                      label="Patient Data Available" 
                      size="small" 
                      color="info" 
                      icon={<AssignmentIcon />}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1}}>
                  {intent.mode_blocked === true && (
                    <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 'bold' }}>
                      {intent.mode_blocked_reason || 'Not available in this mode'}
                    </Typography>
                  )}
                  {intent.prerequisites_met === false && !intent.needs_clarification && intent.mode_blocked !== true && (
                    <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
                      Prerequisites Not Met: {intent.remaining_prerequisites && intent.remaining_prerequisites.length > 0 ? intent.remaining_prerequisites.join(', ') : 'Error fetching prerequisites'}
                    </Typography>
                  )}
                  {intent.isSoftwareIntent && intent.bad_modifier && (
                    <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
                      Modifier Error: {intent.bad_modifier_reason || 'Invalid modifier specified'}
                      {intent.attempted_modifier && ` (attempted: "${intent.attempted_modifier}")`}
                      {intent.attempted_measurement && ` (attempted: ${intent.attempted_measurement.value} ${intent.attempted_measurement.unit})`}
                    </Typography>
                  )}
                </Box>

                {intent.reasoning && !intent.isSoftwareIntent && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Reasoning:</strong> {intent.reasoning}
                  </Typography>
                )}

                {/* Only show patient-specific results if intent was actually executed (not needing clarification, prerequisites met, and not software/nav) */}
                {showPersonaResults && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AssessmentIcon sx={{ fontSize: 16 }} />
                      Patient-Specific Results
                    </Typography>
                    
                    {intent.isStateTransition && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          State Transition:
                        </Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={intent.previous_state_name || intent.previous_state_id || 'Unknown'}
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                          <Typography variant="body2" color="text.secondary">
                            →
                          </Typography>
                          <Chip
                            label={intent.next_state_name || intent.next_state_id || 'Unknown'}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    )}
                    
                    {shouldShowIntentResult(intent) && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Result:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                          {intent.result}
                        </Typography>
                      </Box>
                    )}
                    
                    {evaluationDisplay && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Evaluation:
                        </Typography>
                        <Typography variant="body2" sx={{ color: getEvaluationMuiColor(evaluationDisplay) }}>
                          {evaluationDisplay}
                        </Typography>
                      </Box>
                    )}
                    
                    {intent.fileInfo && (
                      <Box sx={{ 
                        mt: 1,
                        p: 1,
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        borderRadius: 0.5,
                        borderLeft: '3px solid rgba(33, 150, 243, 0.8)'
                      }}>
                        <Typography variant="caption" sx={{ 
                          fontSize: '0.75rem',
                          display: 'block',
                          mb: 0.5,
                          fontWeight: 'bold'
                        }}>
                          📁 Related File: {intent.fileInfo.name}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          href={intent.fileInfo.s3Url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontSize: '0.7rem',
                            py: 0.3,
                            px: 0.8,
                            minWidth: 'auto',
                            backgroundColor: 'rgba(33, 150, 243, 0.8)',
                            '&:hover': {
                              backgroundColor: 'rgba(33, 150, 243, 1)'
                            }
                          }}
                        >
                          📥 Download File
                        </Button>
                      </Box>
                    )}
                  </>
                )}

                {/* Only show "no data" message if intent was executed but has no persona result (not for software/nav intents) */}
                {!showPersonaResults && !intent.needs_clarification && intent.prerequisites_met !== false && intent.mode_blocked !== true && !intent.isSoftwareIntent && !intent.isPatientSelectIntent && !intent.isLabResultsIntent && !intent.isEvalReportIntent && intent.action && intent.object && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    No patient-specific data available for this action-object combination.
                  </Alert>
                )}
              </CardContent>
            </Card>
            );
          })}
        </Box>

        {hasDicomAggregate && dicomFormatted && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box
                onClick={() => setDicomExpanded(!dicomExpanded)}
                role="button"
                aria-label={dicomExpanded ? 'Collapse DICOM message' : 'Expand DICOM message'}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <ImageIcon sx={{ fontSize: 16, color: 'info.main' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  DICOM viewer message (aggregate)
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDicomExpanded(!dicomExpanded);
                  }}
                  sx={{ ml: 'auto' }}
                >
                  {dicomExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={dicomExpanded}>
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Single payload combining all valid imaging intents for the viewer; not tied to one intent row above.
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1,
                      bgcolor: 'background.paper',
                      borderRadius: 0.5,
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: 240,
                      border: '1px solid',
                      borderColor: 'divider',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                    }}
                  >
                    {dicomFormatted.pretty}
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </>
        )}
      </Collapse>
    </Box>
  );
};

export default IntentDisplay;